import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form, HistoryPanel, ListGroup, Spinner, StatusBadge } from '@/components/ui';
import { formatStatusForDisplay } from '../../../utils/statusUtils';
import {
  VendorHistoryDetail,
  VendorHistoryEntry,
  vendorService,
} from '../../../services/vendorService';
import { currency, dateTime, shortDate } from '../utils/format';

type HistoryFilter = 'all' | 'vendor' | 'equipment' | 'rates' | 'documents';

const FILTER_OPTIONS: Array<{ value: HistoryFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'rates', label: 'Rates' },
  { value: 'documents', label: 'Documents' },
];

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  legalName: 'Legal name',
  contactEmail: 'Contact email',
  phoneNumber: 'Phone',
  comments: 'Comments',
  serviceAreas: 'Service areas',
  status: 'Status',
  rate: 'Rate',
  serviceArea: 'Service area',
  effectiveTo: 'Effective to',
  issuedAt: 'Issued',
  expiresAt: 'Expires',
  isActive: 'In service',
  unit: 'Unit',
  isFixedPrice: 'Fixed price',
  makeAndModel: 'Make and model',
  overtimeEligible: 'Overtime eligible',
  equipmentClassification: 'Classification',
  vehicleType: 'Road type',
  requiresVehicleInsurance: 'Requires vehicle insurance',
  vehicleInsuranceExpDate: 'Vehicle insurance expiry',
  vehicleInsuranceFileName: 'Vehicle insurance file',
  customerUnitNumber: 'Unit number',
};

const DATE_FIELDS = new Set(['issuedAt', 'expiresAt', 'effectiveTo', 'vehicleInsuranceExpDate']);

function filterGroup(entityType: string): Exclude<HistoryFilter, 'all'> {
  switch (entityType) {
    case 'RATE':
      return 'rates';
    case 'DOCUMENT':
      return 'documents';
    case 'RESOURCE':
    case 'RESOURCE_AREA':
      return 'equipment';
    default:
      return 'vendor';
  }
}

function formatDetailValue(field: string, value: string | number | null): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (field === 'rate' && typeof value === 'number') return currency(value);
  if (DATE_FIELDS.has(field) && typeof value === 'string') return shortDate(value);
  return String(value);
}

function formatDetail(d: VendorHistoryDetail): string {
  const label = FIELD_LABELS[d.field] ?? d.field;
  return `${label}: ${formatDetailValue(d.field, d.oldValue)} → ${formatDetailValue(d.field, d.newValue)}`;
}

function actorLabel(entry: VendorHistoryEntry): string {
  if (entry.user) {
    const name = `${entry.user.name} ${entry.user.lastName}`.trim();
    return `By ${name || 'Unknown'}`;
  }
  return 'By System';
}

interface VendorHistoryTabProps {
  vendorId: number;
}

const VendorHistoryTab: React.FC<VendorHistoryTabProps> = ({ vendorId }) => {
  const [history, setHistory] = useState<VendorHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<HistoryFilter>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    vendorService
      .getVendorHistory(vendorId)
      .then((rows) => {
        if (!cancelled) setHistory(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load vendor history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorId]);

  const visible = useMemo(
    () => (filter === 'all' ? history : history.filter((row) => filterGroup(row.entityType) === filter)),
    [history, filter]
  );

  return (
    <div>
      <div className="tw-mb-3">
        <label htmlFor="vendor-history-filter" className="tw-mb-1 tw-block tw-text-sm tw-text-content-muted md:tw-hidden">
          Show
        </label>
        <Form.Select
          id="vendor-history-filter"
          className="tw-w-full md:tw-hidden"
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value as HistoryFilter)}
          aria-label="Filter vendor history"
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
        <div className="tw-hidden tw-flex-wrap tw-gap-1 md:tw-flex" role="group" aria-label="History category">
          {FILTER_OPTIONS.map((opt) => {
            const selected = filter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={selected}
                onClick={() => setFilter(opt.value)}
                className={`tw-min-h-[2.75rem] tw-rounded-full tw-border tw-px-3 tw-text-sm ${
                  selected
                    ? 'tw-border-primary tw-bg-primary/10 tw-font-medium tw-text-primary'
                    : 'tw-border-line tw-bg-surface tw-text-content-secondary hover:tw-text-content'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <HistoryPanel timelineTitle="Change history" className="tw-min-h-[16rem]">
        {loading ? (
          <div className="tw-py-4 tw-text-center">
            <Spinner className="tw-text-primary">Loading...</Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : visible.length === 0 ? (
          <Alert variant="info">
            {history.length === 0
              ? 'No changes have been recorded yet. Edits, rate changes, document uploads and approvals will appear here going forward.'
              : 'No changes in this category.'}
          </Alert>
        ) : (
          <ListGroup>
            {visible.map((entry) => (
              <ListGroup.Item key={entry.id}>
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                  <div className="tw-min-w-0 tw-grow">
                    <h6 className="tw-mb-1">{entry.summary || formatStatusForDisplay(entry.stage)}</h6>
                    <p className="tw-mb-1 tw-text-content-muted">
                      <StatusBadge
                        status={entry.stage}
                        label={formatStatusForDisplay(entry.stage)}
                      />
                      {entry.resource?.name ? (
                        <span className="tw-ml-2 tw-text-sm">{entry.resource.name}</span>
                      ) : null}
                    </p>
                    {entry.details && entry.details.length > 0 && (
                      <div className="tw-mb-1">
                        {entry.details.map((d, idx) => (
                          <div key={`${d.field}-${idx}`} className="tw-text-sm">
                            {formatDetail(d)}
                          </div>
                        ))}
                      </div>
                    )}
                    <small className="tw-text-content-muted">{actorLabel(entry)}</small>
                  </div>
                  <small className="tw-ml-2 tw-shrink-0 tw-text-content-muted">{dateTime(entry.createdAt)}</small>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </HistoryPanel>
    </div>
  );
};

export default VendorHistoryTab;
